import type { LocationRef } from "../types";

export interface CarrierLanePreferenceInput {
  carrierMc: string;
  carrierName?: string;
  originRegion: string;
  destinationRegion: string;
  preferredRateCents?: number;
  daysPreferred: string[];
  notes?: string;
}

export interface CapacityMatchLoadInput {
  loadId: string;
  reference: string;
  origin: LocationRef;
  destination: LocationRef;
  pickupWindowStart: string;
  equipmentType: string;
  targetRateCents?: number;
}

export interface CarrierCapacityMatch {
  carrierMc: string;
  carrierName: string;
  originRegion: string;
  destinationRegion: string;
  fitScore: number;
  preferredRateCents?: number;
  targetRateCents?: number;
  reasons: string[];
  notes?: string;
}

export function rankCarrierLanePreferences(args: {
  load: CapacityMatchLoadInput;
  preferences: CarrierLanePreferenceInput[];
  maxResults?: number;
}): CarrierCapacityMatch[] {
  return args.preferences
    .map((preference) => scorePreference(args.load, preference))
    .filter((match) => match.fitScore > 0)
    .sort((left, right) => {
      if (right.fitScore !== left.fitScore) return right.fitScore - left.fitScore;
      return (left.preferredRateCents ?? Number.MAX_SAFE_INTEGER) - (right.preferredRateCents ?? Number.MAX_SAFE_INTEGER);
    })
    .slice(0, args.maxResults ?? 10);
}

export function buildCapacityMatchEvidence(args: {
  load: CapacityMatchLoadInput;
  matches: CarrierCapacityMatch[];
}) {
  return {
    event: "carrier_lane_preference.capacity_match",
    loadReference: args.load.reference,
    lane: `${formatLocation(args.load.origin)} to ${formatLocation(args.load.destination)}`,
    equipmentType: args.load.equipmentType,
    targetRateCents: args.load.targetRateCents,
    matchCount: args.matches.length,
    matches: args.matches.map((match) => ({
      carrierMc: match.carrierMc,
      carrierName: match.carrierName,
      fitScore: match.fitScore,
      reasons: match.reasons,
      preferredRateCents: match.preferredRateCents,
    })),
  };
}

function scorePreference(load: CapacityMatchLoadInput, preference: CarrierLanePreferenceInput): CarrierCapacityMatch {
  const reasons: string[] = [];
  let score = 15;

  const originScore = scoreRegion(preference.originRegion, load.origin);
  const destinationScore = scoreRegion(preference.destinationRegion, load.destination);

  if (originScore > 0) reasons.push(`origin matches ${preference.originRegion}`);
  if (destinationScore > 0) reasons.push(`destination matches ${preference.destinationRegion}`);
  score += originScore + destinationScore;

  if (originScore >= 35 && destinationScore >= 35) {
    score += 10;
    reasons.push("dedicated lane match");
  }

  const dayScore = scorePreferredDay(load.pickupWindowStart, preference.daysPreferred);
  score += dayScore.score;
  if (dayScore.reason) reasons.push(dayScore.reason);

  const rateScore = scorePreferredRate(load.targetRateCents, preference.preferredRateCents);
  score += rateScore.score;
  if (rateScore.reason) reasons.push(rateScore.reason);

  if (preference.notes) reasons.push(preference.notes);

  return {
    carrierMc: preference.carrierMc,
    carrierName: preference.carrierName ?? `MC ${preference.carrierMc}`,
    originRegion: preference.originRegion,
    destinationRegion: preference.destinationRegion,
    fitScore: clamp(Math.round(score), 0, 100),
    preferredRateCents: preference.preferredRateCents,
    targetRateCents: load.targetRateCents,
    reasons,
    notes: preference.notes,
  };
}

function scoreRegion(region: string, location: LocationRef) {
  const normalizedRegion = normalize(region);
  const normalizedState = normalize(location.state);
  const normalizedCity = normalize(location.city);
  const normalizedCityState = normalize(`${location.city} ${location.state}`);

  if (["any", "all", "national", "*"].includes(normalizedRegion)) return 12;
  if (normalizedRegion === normalizedCityState) return 40;
  if (normalizedRegion === normalizedState) return 35;
  if (normalizedRegion === normalizedCity) return 28;
  if (normalizedRegion.includes(normalizedState) || normalizedRegion.includes(normalizedCity)) return 20;
  return -20;
}

function scorePreferredDay(pickupWindowStart: string, daysPreferred: string[]) {
  if (daysPreferred.length === 0) return { score: 5, reason: "no day restrictions" };

  const pickupDay = new Intl.DateTimeFormat("en-US", { weekday: "long", timeZone: "UTC" })
    .format(new Date(pickupWindowStart))
    .toLowerCase();
  const normalizedDays = daysPreferred.map((day) => day.toLowerCase());

  if (normalizedDays.includes("any") || normalizedDays.includes(pickupDay)) {
    return { score: 10, reason: `${pickupDay} matches preferred pickup day` };
  }

  return { score: -6, reason: `${pickupDay} is outside preferred days` };
}

function scorePreferredRate(targetRateCents?: number, preferredRateCents?: number) {
  if (!targetRateCents || !preferredRateCents) return { score: 4, reason: "rate not constrained" };
  if (preferredRateCents <= targetRateCents) return { score: 15, reason: "preferred rate is at or below target" };
  if (preferredRateCents <= targetRateCents * 1.1) return { score: 8, reason: "preferred rate is within 10% of target" };
  return { score: -10, reason: "preferred rate is above target" };
}

function formatLocation(location: LocationRef) {
  return `${location.city}, ${location.state}`;
}

function normalize(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}
